import { useRef, useState, type DragEvent } from 'react';
import { FileText, Upload } from 'lucide-react';
import { formatBytes } from '../../lib/format';

/**
 * Drag-and-drop / click-to-browse file picker. Lifted from customer-app's
 * AddPrivateCandidateModal in Phase 7-dashboard-b. Uniform drop UX for any flow
 * that needs a single-file upload (resumes, logos, etc.).
 *
 * The component owns drag-over state internally; the caller only provides the
 * `onSelect` callback + the `validate` predicate. Errors are surfaced inline.
 */
export interface FileDropZoneProps {
  /** Currently selected file (controlled). null shows the empty placeholder. */
  file: File | null;
  /** Caller's onChange — fires with the new file (already validated) or null on clear. */
  onSelect: (file: File | null) => void;
  /** Caller-supplied validator. Returns null if valid, else a friendly error string. */
  validate?: (file: File) => string | null;
  /** Comma-separated `accept` attribute for the underlying `<input type=file>`. */
  accept?: string;
  /** First-line label (default: "Drop file here, or click to browse"). */
  label?: string;
  /** Second-line hint (e.g., "PDF or DOCX, max 5 MB"). */
  hint?: string;
  /** Disable the input (e.g., during upload). */
  disabled?: boolean;
  /** External error to display (e.g., a server-side rejection); takes precedence over local. */
  externalError?: string | null;
  /** Unique id for the underlying `<input>` so multiple instances on a page don't collide. */
  id?: string;
}

export function FileDropZone({
  file,
  onSelect,
  validate,
  accept = '*',
  label = 'Drop file here, or click to browse',
  hint,
  disabled,
  externalError,
  id = 'qh-filedropzone-input',
}: FileDropZoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const error = externalError ?? localError;

  function handleFile(next: File | null) {
    if (!next) {
      setLocalError(null);
      onSelect(null);
      return;
    }
    if (validate) {
      const msg = validate(next);
      if (msg) {
        setLocalError(msg);
        onSelect(null);
        return;
      }
    }
    setLocalError(null);
    onSelect(next);
  }

  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        onDragOver={(e: DragEvent<HTMLLabelElement>) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e: DragEvent<HTMLLabelElement>) => {
          if (disabled) return;
          e.preventDefault();
          setDragOver(false);
          const dropped = e.dataTransfer.files?.[0] ?? null;
          handleFile(dropped);
        }}
        className={[
          'flex flex-col items-center justify-center text-center gap-2 px-6 py-10 rounded-xl border-2 border-dashed bg-white cursor-pointer transition-colors',
          disabled ? 'opacity-60 cursor-not-allowed' : '',
          dragOver ? 'border-brand-accent bg-brand-secondary/20' : 'border-gray-300',
          error ? 'border-red-300' : '',
        ].join(' ')}
      >
        {file ? (
          <>
            <FileText className="w-8 h-8 text-brand-accent" />
            <p className="text-sm font-bold text-brand-primary break-all">{file.name}</p>
            <p className="text-xs text-brand-muted">{formatBytes(file.size)} · click to choose another</p>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 text-gray-400" />
            <p className="text-sm font-bold text-brand-primary">{label}</p>
            {hint && <p className="text-xs text-brand-muted">{hint}</p>}
          </>
        )}
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={(e) => {
            const picked = e.target.files?.[0] ?? null;
            handleFile(picked);
            // Reset the input so re-selecting the same file fires onChange again.
            if (e.target) e.target.value = '';
          }}
          className="hidden"
        />
      </label>
      {error && <p className="text-xs text-red-700 font-medium">{error}</p>}
    </div>
  );
}
